import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { useState } from 'react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import type { File } from '../types/file.types';
import {
  SelectColumn,
  NameColumn,
  RiskColumn,
  AnomalyColumn,
  CommentColumn,
  HistoryColumn,
  DateColumn,
  SoftwareColumn,
} from '../components/FileList/components/columns';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCurrentPage, setSorting } from '@/store/slices/filesSlice';

const columns = [
  SelectColumn,
  NameColumn,
  RiskColumn,
  AnomalyColumn,
  CommentColumn,
  SoftwareColumn,
  HistoryColumn,
  DateColumn,
];

interface UseFilesTableProps {
  data: File[];
  totalCount: number;
}

export const useFilesTable = ({ data, totalCount }: UseFilesTableProps) => {
  const dispatch = useAppDispatch();
  const { currentPage, pageSize, sorting } = useAppSelector(
    (state) => state.files
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting)
        : updaterOrValue;

    const prev = sorting[0]; // ancienne colonne triée
    const next = newSorting[0]; // nouvelle tentative de tri

    let updatedSorting: SortingState = [];

    // Aucun tri demandé → on l'autorise uniquement pour received_timestamp
    if (!next) {
      if (prev?.id === 'received_timestamp') {
        dispatch(setSorting([])); // autorisé uniquement pour cette colonne
        dispatch(setCurrentPage(0));
      } else if (prev) {
        // toutes les autres → repartent en asc
        updatedSorting = [{ id: prev.id, desc: false }];
        dispatch(setSorting(updatedSorting));
        dispatch(setCurrentPage(0));
      }
      return;
    }

    // Tri sur received_timestamp → on laisse le comportement natif
    if (next.id === 'received_timestamp') {
      dispatch(setSorting([next]));
      dispatch(setCurrentPage(0));
      return;
    }

    // Pour les autres colonnes → cycle forcé entre asc et desc uniquement
    if (prev?.id === next.id) {
      if (prev.desc && next.desc) {
        // desc → retour à asc
        updatedSorting = [{ id: next.id, desc: false }];
      } else {
        // asc → desc ou autre transition
        updatedSorting = [next];
      }
    } else {
      // nouvelle colonne triée → commence toujours à asc
      updatedSorting = [{ id: next.id, desc: false }];
    }

    dispatch(setSorting(updatedSorting));
    dispatch(setCurrentPage(0)); // Retour à la page 0
  };

  const table = useReactTable<File>({
    data,
    columns,
    state: {
      pagination: {
        pageIndex: currentPage,
        pageSize,
      },
      columnFilters,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: handleSortingChange,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater({ pageIndex: currentPage, pageSize })
          : updater;
      dispatch(setCurrentPage(newPagination.pageIndex));
    },
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualSorting: true,
    sortDescFirst: false,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
  });

  return {
    table,
    columnFilters,
  };
};
